import { useField, useFormikContext } from 'formik'

import { FORMIK_EMPTY_VALUE } from '../../../constants/validation'
import { FormField } from '../../Form'

import {
  useFormikFieldChangeConfirmation,
  useFormikFieldClearConfirmation,
} from './FormikField.hooks'
import {
  getFormikFieldContainerDisabledProps,
  getFormikFieldContainerValueProps,
  getFormikFieldInputErrorProps,
  getFormikFieldNextValue,
  getFormikFieldValueProps,
} from './FormikField.utils'

function FormikField({
  Component,
  Field,
  changeConfirmationProps,
  confirmationChange,
  confirmationProps,
  disabled,
  elementRef,
  emptyValue = FORMIK_EMPTY_VALUE,
  fieldComponentProps,
  getErrorProps = getFormikFieldInputErrorProps,
  getFieldDisabledProps = getFormikFieldContainerDisabledProps,
  getFieldValueProps = getFormikFieldContainerValueProps,
  getNextValue = getFormikFieldNextValue,
  getValueProps = getFormikFieldValueProps,
  qaName,
  setModal,
  shouldConfirmClear,
  onChange,
  onClear,
  ...fieldProps
}) {
  const { isSubmitting } = useFormikContext()
  const [field, meta, helpers] = useField(fieldProps)

  const fieldDisabled = isSubmitting || disabled

  const { handleOpenConfirmationModal } = useFormikFieldClearConfirmation({
    confirmationProps,
    helpers,
    setModal,
  })

  function handleSubmitChange(value) {
    onChange?.(value)

    // backwards compatibility with CustomSelect
    // CustomSelect component doesn't contain value.type, return object like {value: .., label: ..}
    value?.type
      ? field.onChange(getNextValue(value))
      : helpers.setValue(getNextValue(value, field.value))

    helpers.setTouched(true)
  }

  const { handleOpenChangeConfirmationModal } =
    useFormikFieldChangeConfirmation({
      changeConfirmationProps,
      setModal,
      onSubmit: handleSubmitChange,
    })

  function handleChange(value) {
    if (confirmationChange) {
      handleOpenChangeConfirmationModal(value)

      return
    }

    handleSubmitChange(value)
  }

  function handleClear() {
    if (shouldConfirmClear) {
      handleOpenConfirmationModal()
    } else {
      onClear?.()

      helpers.setValue(emptyValue)
    }
  }

  const errorProps = getErrorProps(meta.error)

  const fieldDisabledProps = getFieldDisabledProps({
    Field,
    disabled: fieldDisabled,
    isSubmitting,
  })

  const fieldValueProps = getFieldValueProps({ Field, field })

  // Can be used for customize `value` name prop
  const valueProps = getValueProps(field)

  return (
    <FormField
      {...fieldComponentProps}
      {...fieldDisabledProps}
      {...fieldValueProps}
      Component={Field}
    >
      <Component
        {...fieldProps}
        {...errorProps}
        {...valueProps}
        disabled={fieldDisabled}
        qaName={qaName || fieldProps.name}
        ref={elementRef}
        onChange={handleChange}
        onClear={handleClear}
      />
    </FormField>
  )
}

export default FormikField
